import { Fragment } from "react";
import { FieldTableName } from "../../fields/FieldTableName";
import { FieldTableKey } from "../../fields/FieldTableKey";
import { EuiSpacer, EuiText } from "@inscopix/ideas-eui";
import { FieldTool } from "../../fields/FieldTool";
import {
  isToolSpecV2,
  isToolSpecV3,
  ToolSpec,
} from "types/json-schemas/type_guards";

interface StepTableInfoProps {
  toolSpec: ToolSpec | undefined;
  setToolName: (toolName: string) => void;
  setToolSpec: (toolSpec: ToolSpec | undefined) => void;
  setToolVersionId: (toolVersionId: number | undefined) => void;
  tableName: { value: string; isDirty: boolean };
  setTableName: (tableName: string, isDirty: boolean) => void;
  tableKey: { value: string; isDirty: boolean };
  setTableKey: (tableKey: string, isDirty: boolean) => void;
  columnGroupName: { value: string; isDirty: boolean };
  setColumnGroupName: (columnGroupName: string, isDirty: boolean) => void;
  paramKeys: { value: string[]; isDirty: boolean };
  setParamKeys: (paramKeys: string[], isDirty: boolean) => void;
}

export const StepTableInfo = ({
  toolSpec,
  setToolName,
  setToolSpec,
  setToolVersionId,
  tableName,
  setTableName,
  tableKey,
  setTableKey,
  columnGroupName,
  setColumnGroupName,
  paramKeys,
  setParamKeys,
}: StepTableInfoProps) => {
  return (
    <Fragment>
      <EuiText>
        <h4>Table info</h4>
      </EuiText>

      <EuiSpacer size="s" />

      <EuiText size="s">
        <p>Select an analysis for the table to use.</p>
      </EuiText>

      <EuiSpacer />

      <FieldTool
        value={toolSpec}
        onChange={(toolName, spec, toolVersionId) => {
          setToolSpec(spec);
          setToolVersionId(toolVersionId);
          const defaultTableName = toolName ?? "";

          if (toolName !== undefined) {
            setToolName(toolName);
          }

          if (!tableName.isDirty) {
            setTableName(defaultTableName, false);
          }

          if (!columnGroupName.isDirty) {
            setColumnGroupName(defaultTableName, false);
          }

          if (!paramKeys.isDirty) {
            let resultKeys: string[] = [];

            if (isToolSpecV3(spec)) {
              resultKeys = spec.results.map((r) => r.key);
            } else if (isToolSpecV2(spec)) {
              resultKeys = spec.results.flatMap(
                (result) => result.files?.map((file) => file.result_key) ?? [],
              );
            }

            setParamKeys(resultKeys ?? [], false);
          }
        }}
      />

      <FieldTableName
        label="Table name"
        value={tableName.value}
        onChange={({ value }) => {
          setTableName(value, true);

          if (!tableKey.isDirty) {
            const defaultKey = value.slice(0, 3).toUpperCase();
            setTableKey(defaultKey, false);
          }

          if (!columnGroupName.isDirty) {
            setColumnGroupName(value, false);
          }
        }}
      />

      <FieldTableKey
        label="Table key"
        value={tableKey.value}
        onChange={({ value }) => {
          setTableKey(value, true);
        }}
        tableName={tableName.value}
        autoGenerate={!tableKey.isDirty}
        onAutoGenerate={(key) => {
          setTableKey(key, false);
        }}
      />
    </Fragment>
  );
};
